import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, useMediaQuery, useTheme, Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { collection, query, orderBy, limit, getDocs, startAfter, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../../firebase_config';

const PAGE_SIZE = 15;

const Transactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [stats, setStats] = useState({ totalCount: 0, totalVolume: 0, successRate: 0, failedCount: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false); // <-- New state for CSV export
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');

  const observer = useRef();

  // 1. Fetch Stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statsRef = doc(db, 'admin_stats', 'transactions'); 
        const statsSnap = await getDoc(statsRef);
        if (statsSnap.exists()) {
          setStats(statsSnap.data());
        } else {
          setStats({ totalCount: 0, totalVolume: 0, successRate: 0, failedCount: 0 });
          // setStats({ totalCount: 8542, totalVolume: 2840000, successRate: 96.5, failedCount: 298 });
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  // Helper to build the query dynamically
  const buildQuery = (lastDoc = null, ignoreLimit = false) => {
    let constraints = [];
    
    if (typeFilter !== 'All Types') constraints.push(where('type', '==', typeFilter));
    if (statusFilter !== 'All Status') constraints.push(where('status', '==', statusFilter));

    constraints.push(orderBy('createdAt', 'desc'));
    
    if (lastDoc) constraints.push(startAfter(lastDoc));
    if (!ignoreLimit) constraints.push(limit(PAGE_SIZE));

    return query(collection(db, 'transactions'), ...constraints);
  };

  // 2. Fetch Initial Transactions
  const fetchInitialTransactions = async () => {
    setLoading(true);
    setTransactions([]);
    try {
      const q = buildQuery();
      const snapshot = await getDocs(q);
      
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTransactions(docs);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter]);

  // 3. Infinite Scroll trigger
  const fetchMoreTransactions = async () => {
    if (!hasMore || loadingMore || !lastVisible) return;
    setLoadingMore(true);
    try {
      const q = buildQuery(lastVisible);
      const snapshot = await getDocs(q);
      
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTransactions(prev => [...prev, ...docs]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching more transactions:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const lastElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchMoreTransactions();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);


  // 4. Client-side Search Filtering
  const displayedTransactions = transactions.filter(tx => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      tx.id.toLowerCase().includes(term) || 
      (tx.tx_ref && tx.tx_ref.toLowerCase().includes(term)) ||
      (tx.userId && tx.userId.toLowerCase().includes(term))
    );
  });

  // ==========================================
  // 5. CSV Export Functionality
  // ==========================================
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Build the query ignoring the pagination limit to get ALL filtered data
      const q = buildQuery(null, true);
      const snapshot = await getDocs(q);
      
      // Define CSV headers
      const headers = ['Date', 'Time', 'Transaction ID', 'User ID', 'Type', 'Amount (ETB)', 'Status', 'Gateway ID'];
      
      // Map data to CSV rows, wrapping fields in quotes to prevent comma-breakage
      const rows = snapshot.docs.map(doc => {
        const data = doc.data();
        const dt = formatDate(data.createdAt);
        return [
          `"${dt !== 'N/A' ? dt.date : 'N/A'}"`,
          `"${dt !== 'N/A' ? dt.time : 'N/A'}"`,
          `"${doc.id}"`,
          `"${data.userId || ''}"`,
          `"${data.type || ''}"`,
          `"${data.amount || 0}"`,
          `"${data.status || ''}"`,
          `"${data.tx_ref || 'INTERNAL'}"`
        ].join(',');
      });

      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows].join('\n');
      
      // Create a Blob and trigger browser download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      // Generate filename with timestamp
      const filename = `transactions_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export CSV. Please try again.");
    } finally {
      setExporting(false);
    }
  };


  // Helper formatting
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success': return { bg: '#e6f4ea', text: '#1e8e3e' };
      case 'failed': return { bg: '#fce8e6', text: '#d93025' };
      case 'pending': return { bg: '#fef7e0', text: '#f9ab00' };
      default: return { bg: '#f1f3f4', text: '#5f6368' };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', { maximumFractionDigits: 0 }).format(Math.abs(amount || 0));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return {
      date: date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    };
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Transaction Overview</Typography>
          <Typography variant="body2" color="text.secondary">Monitor all system transactions</Typography>
        </Box>
        {/* UPDATED BUTTON HERE */}
        <Button 
          variant="outlined" 
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />} 
          onClick={handleExportCSV}
          disabled={exporting}
          sx={{ textTransform: 'none', color: 'text.primary', borderColor: 'divider', display: { xs: 'none', sm: 'flex' } }}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>

      {/* Aggregate Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Total Transactions</Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{stats.totalCount.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Total Volume</Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{stats.totalVolume.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>ETB</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Success Rate</Typography>
              <Typography variant="h4" fontWeight="bold" color="#1e8e3e" sx={{ mt: 1 }}>{stats.successRate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Failed Transactions</Typography>
              <Typography variant="h4" fontWeight="bold" color="#d93025" sx={{ mt: 1 }}>{stats.failedCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Bar */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 2, 
        mb: 4, 
        p: 2, 
        bgcolor: '#fff',
        border: '1px solid', 
        borderColor: 'divider', 
        borderRadius: 3 
      }}>
        <TextField
          placeholder="Search IDs..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} /> }}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: { xs: '100%', md: 160 } }}
        >
          <MenuItem value="All Types">All Types</MenuItem>
          <MenuItem value="session_lock">Session Payment</MenuItem>
          <MenuItem value="top-up">Top Up</MenuItem>
          <MenuItem value="withdrawal_request">Withdrawal</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: { xs: '100%', md: 160 } }}
        >
          <MenuItem value="All Status">All Status</MenuItem>
          <MenuItem value="success">Success</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>
        <Button 
          variant="outlined" 
          startIcon={<FilterListOffIcon />}
          sx={{ 
            textTransform: 'none', 
            whiteSpace: 'nowrap', 
            flexShrink: 0,
            color: 'text.primary',
            borderColor: 'divider'
          }}
          onClick={() => { setSearchTerm(''); setTypeFilter('All Types'); setStatusFilter('All Status'); }}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Data View */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : displayedTransactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography variant="body1">No transactions found matching your filters.</Typography>
        </Box>
      ) : isMobile ? (
        
        <Stack spacing={2}>
          {displayedTransactions.map((tx, index) => {
            const statusColors = getStatusColor(tx.status);
            const isLastElement = displayedTransactions.length === index + 1;
            const dt = formatDate(tx.createdAt);
            
            return (
              <Card key={tx.id} variant="outlined" sx={{ borderRadius: 3 }} ref={isLastElement ? lastElementRef : null}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{dt.date} • {dt.time}</Typography>
                    <Chip label={tx.status || 'Unknown'} size="small" sx={{ bgcolor: statusColors.bg, color: statusColors.text, fontWeight: 'bold', height: 22, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2, color: 'text.secondary' }}>{tx.id}</Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      <Chip label={tx.type?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>User: {tx.userId?.substring(0, 6)}...</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {formatCurrency(tx.amount)} ETB
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>

      ) : (

        <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 3, boxShadow: 'none' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>Date & Time</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>Transaction ID</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>User ID</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>Type</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>Amount</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, py: 2 }}>Gateway ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedTransactions.map((tx, index) => {
                const statusColors = getStatusColor(tx.status);
                const isLastElement = displayedTransactions.length === index + 1;
                const dt = formatDate(tx.createdAt);

                return (
                  <TableRow key={tx.id} hover ref={isLastElement ? lastElementRef : null}>
                    <TableCell>
                      <Typography variant="body2">{dt.date}</Typography>
                      <Typography variant="caption" color="text.secondary">{dt.time}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{tx.id.substring(0, 10)}...</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{tx.userId?.substring(0, 8)}...</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={tx.type?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize', color: 'text.secondary', borderColor: 'divider' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{formatCurrency(tx.amount)} ETB</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={tx.status || 'Unknown'} size="small" sx={{ bgcolor: statusColors.bg, color: statusColors.text, fontWeight: 'bold', textTransform: 'capitalize', border: 'none' }} />
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{tx.tx_ref?.substring(0,10) || 'INTERNAL'}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={30} />
        </Box>
      )}
    </Box>
  );
};

export default Transactions;